import ReactDOM from 'react-dom';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import React, { useEffect, useState } from 'react';
import { Dimmer, Dropdown, DropdownItemProps } from 'semantic-ui-react';
import { getSizeNumbers } from 'src/scripts/templates/product/components/VariantSelections/helpers';
import { ga4options } from '../../../../../templates/product-listing-page/quick-add/QuickAddPanel';
import { CartVariantData, addVariant, getCartData } from '../../../../../utils/cart';
import { ISizesData } from './UpsellProductCards';
import { dispatchAddingToCart, OPEN_VARIANT_SELECTOR_DROPDOWN } from './utils';
import '../../../../../templates/product-listing-page/quick-add/quick-add-styles.scss';
import PurchaseLimitMessaging, {
  hasMetLimitedProductCartQuantity,
  PurchaseLimitTagDataTypes,
} from 'src/scripts/templates/_shared/ui-components/PurchaseLimitItem/PurchaseLimitMessaging';
import QuickFit from 'src/scripts/templates/product/components/QuickFit';
import { UrgencyMessage } from 'src/scripts/templates/product/components/ScarcityMessaging/UrgencyMessage';
import { checkIfItemCanBeSoldOut } from 'src/scripts/utils/helpers';

// renderTargetContainer: parent element node: where to append the dropdown menu in desktopview. *getDesktopDropdownTarget
let renderTargetContainer: HTMLElement | null = null;

// [REVISIT] Move to this file and all related components into one dedicated folder; This component is global and should not be in the Cart Impulse folder
const QuickAddVariantSelector = () => {
  const [data, setData] = useState<VariantSelectorData | null>(null);
  const [isAnimated, setAnimated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const isMobileAndTablet = useMediaQuery('(max-width: 1199.98px)');

  useEffect(() => {
    const openVariantSelector = (e: Event) => {
      const eventData = e as CustomEvent<VariantSelectorData>;
      setData(eventData.detail);
    };

    document.addEventListener(OPEN_VARIANT_SELECTOR_DROPDOWN, openVariantSelector);
    return () => {
      document.removeEventListener(OPEN_VARIANT_SELECTOR_DROPDOWN, openVariantSelector);
    };
  }, []);

  useEffect(() => {
    if (data) {
      setIsOpen(true);
      handleAnimation(true);
      if (data?.renderTargetContainer) {
        renderTargetContainer = data?.renderTargetContainer;
      }
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleAnimation = (value: boolean) => {
    // ? [REVISIT] this can produce weird effects when resizing browser width around the breakpoint (mostly developers)
    if (isMobileAndTablet) {
      setTimeout(() => {
        setAnimated(value);
      }, 0);
    }
  };

  const closeSizeSelector = () => {
    document.body.classList.remove('locked');
    setIsOpen(false);
    renderTargetContainer = null;
    handleAnimation(false);
  };

  const dropdownOptions = data?.sizesData.map((size) => {
    const purchaseLimitTagData = data && data.purchaseLimitTagData;
    const cartData = getCartData();
    const hasMetATCLimit =
      purchaseLimitTagData && size.sku
        ? hasMetLimitedProductCartQuantity(cartData, size.sku, purchaseLimitTagData)
        : undefined;

    const { quantityAvailable, isSizeAvailable } = size;
    const shouldShowUrgencyMessage = checkIfItemCanBeSoldOut(data.variantPricesData.tags)
      ? !(hasMetATCLimit || !isSizeAvailable)
      : false;

    const isDisabled = !size.isSizeAvailable || hasMetATCLimit;

    return {
      key: size.value,
      value: size.value,
      content: (
        <>
          <div
            className="size-option small-p black"
            onClick={() => {
              if (isDisabled) return;
              const variantId = size.variantId;
              const variantPricesData = {
                ...data.variantPricesData,
                id: Number(variantId),
              };
              variantId &&
                !hasMetATCLimit &&
                addVariant(
                  variantId,
                  variantPricesData,
                  { shouldShowATCmodal: data.shouldShowATCmodal, ATBtype: data.ATBtype },
                  data.ga4options
                );
              if (data?.mountNode) dispatchAddingToCart(data?.mountNode);
            }}
          >
            {size.value} {getSizeNumbers(size.value!, data.productType, size.sku)}
            {!size.isSizeAvailable ? (
              ' - Sold Out Online'
            ) : hasMetATCLimit && purchaseLimitTagData ? (
              <>
                {' - '}
                <PurchaseLimitMessaging purchaseLimitTagData={purchaseLimitTagData} messageType={'variant'} />
              </>
            ) : null}
            {shouldShowUrgencyMessage ? (
              <UrgencyMessage
                prependHyphen={true}
                quantityLeft={quantityAvailable}
                className="list-quick-add"
                style={{ color: '#605E5C' }}
              />
            ) : null}
          </div>
          <Dropdown.Divider />
        </>
      ),
      disabled: isDisabled,
    } as DropdownItemProps;
  });

  const header = (
    <>
      <div className="header">
        Select Size
        <div role="button" className="selector-close-icon" onClick={closeSizeSelector}></div>
        {data?.quickFitSizeSelectorText ? (
          <QuickFit quickFitText={data.quickFitSizeSelectorText.value} showFitPrefix={false} />
        ) : null}
      </div>
    </>
  );
  // position quick add dropdown in  desktop view
  const dropdownStyle =
    !isMobileAndTablet && data?.mountNode && renderTargetContainer
      ? calculateDropdownPosition(data?.mountNode, renderTargetContainer)
      : {};

  const DropdownMenu = (
    <div
      style={
        isOpen && isMobileAndTablet
          ? { position: 'fixed', width: '100vw', height: '100vh', top: 0, left: 0, zIndex: 201 }
          : {}
      }
    >
      <Dropdown
        closeOnChange
        floating
        icon={false}
        open={isOpen}
        className={`quick-add-variant-selection-dropdown button ${isAnimated ? 'animated' : ''}`}
        onClose={closeSizeSelector}
        style={dropdownStyle}
        options={dropdownOptions}
        header={header}
      />
      {isMobileAndTablet && <Dimmer onClick={closeSizeSelector} style={{ opacity: '0.65' }} active={isOpen} />}
    </div>
  );

  if (renderTargetContainer && !isMobileAndTablet) {
    return ReactDOM.createPortal(DropdownMenu, getDesktopDropdownTarget(renderTargetContainer));
  }
  return DropdownMenu;
};

export { QuickAddVariantSelector };

const getDesktopDropdownTarget = (parentElement: HTMLElement) => {
  const existingPortalTarget = parentElement.querySelector("[react-render-target='variant-dropdown-menu']");
  if (!existingPortalTarget) {
    const newPortalTarget = document.createElement('div');
    newPortalTarget.setAttribute('react-render-target', 'variant-dropdown-menu');
    parentElement.appendChild(newPortalTarget);
    return newPortalTarget;
  } else {
    return existingPortalTarget;
  }
};

// calculates the position of a dropdown menu relative to a button element
const calculateDropdownPosition = (button: Element, parentElement: HTMLElement | null) => {
  const windowWidth = document.documentElement.clientWidth;
  const dropdownWidth = 250; // Fixed dropdown width
  if (!parentElement) {
    return { left: 0 };
  }
  const { left: buttonLeft } = button.getBoundingClientRect();
  const { left: containerLeft, width: containerWidth } = parentElement.getBoundingClientRect();
  // If there is enough space to the right of the button, position the dropdown to the right of the button
  if (windowWidth >= buttonLeft + dropdownWidth) {
    return { left: buttonLeft - containerLeft };
  } else {
    // If there is not enough space to the right of the button, position the dropdown to the left of the button
    return { right: containerWidth + containerLeft - buttonLeft + dropdownWidth };
  }
};

export const renderQuickAddVariantSelector = () => {
  ReactDOM.render(<QuickAddVariantSelector />, document.querySelector("[react-render-target='QuickAddContainer']"));
};

export interface VariantSelectorData {
  ATBtype: string;
  shouldShowATCmodal: boolean;
  sizesData: ISizesData[];
  productType: string;
  mountNodePosition?: number;
  mountNode?: HTMLElement;
  variantPricesData: CartVariantData;
  ga4options?: ga4options;
  purchaseLimitTagData?: PurchaseLimitTagDataTypes;
  quickFitSizeSelectorText?: IQuickFitSizeSelectorData;
  renderTargetContainer?: HTMLElement | null;
}

export interface IQuickFitSizeSelectorData {
  value: string;
}
